import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DocumentTextIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  SparklesIcon,
  PencilSquareIcon,
  BeakerIcon
} from '@heroicons/react/24/outline';
import apiClient from '../services/api';

// Diff highlighting function
const highlightDifferences = (original, improved) => {
  if (!original || !improved) return { originalHighlighted: original || '', improvedHighlighted: improved || '' };

  const originalWords = original.split(/\s+/);
  const improvedWords = improved.split(/\s+/);

  let originalHighlighted = '';
  let improvedHighlighted = '';

  // Simple word-based diff
  const maxLen = Math.max(originalWords.length, improvedWords.length);

  for (let i = 0; i < maxLen; i++) {
    const origWord = originalWords[i] || '';
    const impWord = improvedWords[i] || '';

    if (origWord !== impWord) {
      if (origWord) {
        originalHighlighted += `<span class="bg-red-200 dark:bg-red-900/50 px-0.5 rounded">${origWord}</span> `;
      }
      if (impWord) {
        improvedHighlighted += `<span class="bg-green-200 dark:bg-green-900/50 px-0.5 rounded">${impWord}</span> `;
      }
    } else {
      originalHighlighted += `${origWord} `;
      improvedHighlighted += `${impWord} `;
    }
  }

  return { originalHighlighted, improvedHighlighted };
};

const SingleRequirementAnalysis = ({ userId, userEmail, selectedRulesFile }) => {
  const [requirementText, setRequirementText] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [editedImprovement, setEditedImprovement] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [reAnalysisResult, setReAnalysisResult] = useState(null);
  const [isReAnalyzing, setIsReAnalyzing] = useState(false);
  const [defaultRulesType, setDefaultRulesType] = useState('PLM');

  // Progress tracking
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisStage, setAnalysisStage] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(null);
  const elapsedTimerRef = React.useRef(null);
  const analysisStartTimeRef = React.useRef(null);

  // Extract filename from selectedRulesFile (could be object or string)
  const rulesFileName = typeof selectedRulesFile === 'string'
    ? selectedRulesFile
    : selectedRulesFile?.name || null;

  const DEFAULT_PLM_RULES_FILE = 'plm_default_rules.xlsx';
  const DEFAULT_FUSA_RULES_FILE = 'fusa_default_rules.xlsx';

  useEffect(() => {
    if (currentAnalysis) {
      setEditedImprovement(currentAnalysis.improved_requirement);
    }
  }, [currentAnalysis]);

  // Timer effect
  useEffect(() => {
    if (isAnalyzing || isReAnalyzing) {
      // Start timer
      analysisStartTimeRef.current = Date.now();
      setElapsedTime(0);

      elapsedTimerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - analysisStartTimeRef.current) / 1000);
        setElapsedTime(elapsed);

        // Update estimated time remaining based on progress
        if (analysisProgress > 0 && analysisProgress < 100) {
          const estimatedTotal = elapsed / (analysisProgress / 100);
          const remaining = Math.max(0, estimatedTotal - elapsed);
          setEstimatedTimeRemaining(remaining);
        }
      }, 1000);
    } else {
      // Clear timer
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
        elapsedTimerRef.current = null;
      }
      setElapsedTime(0);
      setEstimatedTimeRemaining(null);
    }

    return () => {
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
      }
    };
  }, [isAnalyzing, isReAnalyzing, analysisProgress]);

  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined || seconds < 0) {
      return 'Calculating...';
    }

    const totalSeconds = Number(seconds);
    if (isNaN(totalSeconds)) {
      return 'Calculating...';
    }

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = Math.floor(totalSeconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const handleAnalyze = async () => {
    if (!requirementText.trim()) {
      setError('Please enter a requirement to analyze');
      return;
    }

    if (requirementText.length > 5000) {
      setError(`Requirement text is too long (${requirementText.length}/5000 characters)`);
      return;
    }

    setIsAnalyzing(true);
    setError('');
    setAnalysisProgress(0);
    setAnalysisStage('Preparing analysis...');

    try {
      // Stage 1: Downloading rules (10% progress)
      setAnalysisStage('Downloading rules file...');
      setAnalysisProgress(10);
      const formData = new FormData();
      formData.append('requirement_text', requirementText.trim());

      // Get the rules file from the fileList (or use default rules)
      const useDefaultRules = !rulesFileName;
      const defaultRulesFile = defaultRulesType === 'FuSa' ? DEFAULT_FUSA_RULES_FILE : DEFAULT_PLM_RULES_FILE;
      const effectiveRulesFileName = rulesFileName || defaultRulesFile;

      const rulesFileResponse = await apiClient.post(
        `/files/${userId}/download`,
        {
          file_type: 'rules',
          file_name: effectiveRulesFileName,
          default_rules: useDefaultRules,
          default_rules_file_name: useDefaultRules ? defaultRulesFile : undefined
        },
        { responseType: 'blob' }
      );

      const rulesBlob = new Blob([rulesFileResponse.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      formData.append('rules_file', rulesBlob, effectiveRulesFileName);

      // Stage 2: Analyzing against rules (30-80% progress)
      setAnalysisStage('Analyzing requirement against rules...');
      setAnalysisProgress(30);

      const response = await apiClient.post(
        '/api/analyze/single-requirement',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      // Stage 3: Processing results (90% progress)
      setAnalysisStage('Processing results...');
      setAnalysisProgress(90);

      setCurrentAnalysis(response.data);
      setEditedImprovement(response.data.improved_requirement);
      setIsEditing(false);

      // Add to history
      setAnalysisHistory(prev => [
        {
          ...response.data,
          id: Date.now(),
          timestamp: new Date().toISOString()
        },
        ...prev
      ]);

      // Complete!
      setAnalysisStage('Complete!');
      setAnalysisProgress(100);

    } catch (err) {
      console.error('Analysis error:', err);
      let errorMessage = 'Failed to analyze requirement';

      if (err.response?.data?.detail) {
        errorMessage = typeof err.response.data.detail === 'string'
          ? err.response.data.detail
          : JSON.stringify(err.response.data.detail);
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setAnalysisProgress(0);
      setAnalysisStage('');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleReAnalyze = async () => {
    if (!editedImprovement.trim()) {
      setError('No text to re-analyze');
      return;
    }

    setIsReAnalyzing(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('requirement_text', editedImprovement.trim());

      // Get the rules file from the fileList (or use default rules)
      const useDefaultRules = !rulesFileName;
      const defaultRulesFile = defaultRulesType === 'FuSa' ? DEFAULT_FUSA_RULES_FILE : DEFAULT_PLM_RULES_FILE;
      const effectiveRulesFileName = rulesFileName || defaultRulesFile;

      const rulesFileResponse = await apiClient.post(
        `/files/${userId}/download`,
        {
          file_type: 'rules',
          file_name: effectiveRulesFileName,
          default_rules: useDefaultRules,
          default_rules_file_name: useDefaultRules ? defaultRulesFile : undefined
        },
        { responseType: 'blob' }
      );

      const rulesBlob = new Blob([rulesFileResponse.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      formData.append('rules_file', rulesBlob, effectiveRulesFileName);

      const response = await apiClient.post(
        '/api/analyze/single-requirement',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      // Store re-analysis result separately
      setReAnalysisResult(response.data);

    } catch (err) {
      console.error('Re-analysis error:', err);
      let errorMessage = 'Failed to re-analyze requirement';

      if (err.response?.data?.detail) {
        errorMessage = typeof err.response.data.detail === 'string'
          ? err.response.data.detail
          : JSON.stringify(err.response.data.detail);
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsReAnalyzing(false);
    }
  };

  const handleDownloadJSON = () => {
    if (!currentAnalysis) return;

    const dataToDownload = {
      ...currentAnalysis,
      improved_requirement: editedImprovement // Use edited version
    };

    const blob = new Blob([JSON.stringify(dataToDownload, null, 2)], {
      type: 'application/json'
    });

    const defaultFilename = `requirement_analysis_${new Date().toISOString().slice(0, 10)}_${Date.now()}.json`;
    const filename = prompt('Enter filename:', defaultFilename);

    if (filename) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename.endsWith('.json') ? filename : `${filename}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const loadHistoryItem = (item) => {
    setCurrentAnalysis(item);
    setRequirementText(item.original_requirement);
    setEditedImprovement(item.improved_requirement);
    setIsEditing(false);
    setShowHistory(false);
  };

  const diff = currentAnalysis
    ? highlightDifferences(currentAnalysis.original_requirement, editedImprovement)
    : { originalHighlighted: '', improvedHighlighted: '' };

  const criticalFailedRules = currentAnalysis?.rule_checks?.filter(r => !r.passed && r.critical !== false) || [];
  const warningRules = currentAnalysis?.rule_checks?.filter(r => !r.passed && r.critical === false) || [];
  const passedRules = currentAnalysis?.rule_checks?.filter(r => r.passed) || [];

  return (
    <div className="w-full max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BeakerIcon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
            Single Requirement Analysis
          </h2>
        </div>

        {analysisHistory.length > 0 && (
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 px-4 py-2 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-lg transition-colors"
          >
            <ClockIcon className="w-5 h-5" />
            <span>History ({analysisHistory.length})</span>
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Rules File Status */}
      {rulesFileName ? (
        <div className={`p-4 rounded-lg border ${
          'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
        }`}>
          <div className="flex items-center gap-2">
            <>
              <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
              <span className="text-green-700 dark:text-green-400">
                Rules file loaded: <strong>{rulesFileName}</strong>
              </span>
            </>
          </div>
        </div>
          ) : (
        <div className={`p-4 rounded-lg border ${
          'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
        }`}>
            <div className="flex items-center gap-2">
              <InformationCircleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              <span className="text-yellow-700 dark:text-yellow-400">
                No rules file selected. Please select which default rules to use:
              </span>
              <select
                value={defaultRulesType}
                onChange={(e) => setDefaultRulesType(e.target.value)}
                className="ml-2 px-3 py-1 bg-white dark:bg-neutral-800 border border-yellow-300 dark:border-yellow-700 rounded-md text-sm font-medium text-yellow-700 dark:text-yellow-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="PLM">PLM</option>
                <option value="FuSa">FuSa</option>
              </select>
            </div>
        </div>)}

      {/* Input Section */}
      <div className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
            Requirement Text
          </label>
          <span className={`text-sm ${
            requirementText.length > 5000
              ? 'text-red-600 dark:text-red-400 font-semibold'
              : 'text-neutral-500 dark:text-neutral-400'
          }`}>
            {requirementText.length} / 5000
          </span>
        </div>

        <textarea
          value={requirementText}
          onChange={(e) => setRequirementText(e.target.value)}
          placeholder="Enter your requirement text here..."
          className="w-full h-32 px-4 py-3 border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-800 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none text-neutral-900 dark:text-neutral-100"
          maxLength={5000}
        />

        {/* Analyze Button */}
        <button
          onClick={handleAnalyze}
          disabled={isAnalyzing || !requirementText.trim()}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-neutral-400 dark:disabled:bg-neutral-600 text-white rounded-lg font-medium transition-colors disabled:cursor-not-allowed"
        >
          {isAnalyzing ? (
            <>
              <ArrowPathIcon className="w-5 h-5 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <SparklesIcon className="w-5 h-5" />
              Analyze Requirement
            </>
          )}
        </button>
      </div>

      {/* Progress Bar */}
      {(isAnalyzing || isReAnalyzing) && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 p-6"
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ArrowPathIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 animate-spin" />
                <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  {analysisStage || 'Processing...'}
                </span>
              </div>
              <span className="text-sm text-neutral-600 dark:text-neutral-400">
                {analysisProgress}%
              </span>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-neutral-200 dark:bg-neutral-800 rounded-full h-2 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${analysisProgress}%` }}
                transition={{ duration: 0.3 }}
                className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full"
              />
            </div>

            {/* Time information */}
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400">
                <ClockIcon className="w-4 h-4" />
                <span>Elapsed: {formatTime(elapsedTime)}</span>
              </div>
              {estimatedTimeRemaining !== null && estimatedTimeRemaining > 0 && (
                <span className="text-neutral-600 dark:text-neutral-400">
                  Est. remaining: {formatTime(estimatedTimeRemaining)}
                </span>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Results Section */}
      {currentAnalysis && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Rule Check Results */}
          <div className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 p-6">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
              Rule Analysis Results
            </h3>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                  <CheckCircleIcon className="w-6 h-6" />
                  <div>
                    <div className="text-2xl font-bold">{passedRules.length}</div>
                    <div className="text-sm">Passed</div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                  <ExclamationTriangleIcon className="w-6 h-6" />
                  <div>
                    <div className="text-2xl font-bold">{criticalFailedRules.length}</div>
                    <div className="text-sm">Failures</div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                  <InformationCircleIcon className="w-6 h-6" />
                  <div>
                    <div className="text-2xl font-bold">{warningRules.length}</div>
                    <div className="text-sm">Warnings</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Failed Rules (Critical) */}
            {criticalFailedRules.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium text-red-700 dark:text-red-400 mb-2">Failed Rules (Critical):</h4>
                <div className="space-y-2">
                  {criticalFailedRules.map((rule, idx) => (
                    <div key={idx} className="p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg">
                      <div className="font-medium text-sm text-red-900 dark:text-red-300">
                        Rule {String(rule.rule_number || idx + 1)}
                      </div>
                      <div className="text-sm text-red-700 dark:text-red-400 mt-1">
                        {String(rule.explanation || 'No explanation provided')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Warning Rules (Non-Critical) */}
            {warningRules.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium text-amber-700 dark:text-amber-400 mb-2">Warning Rules (Non-Critical):</h4>
                <div className="space-y-2">
                  {warningRules.map((rule, idx) => (
                    <div key={idx} className="p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg">
                      <div className="font-medium text-sm text-amber-900 dark:text-amber-300">
                        Rule {String(rule.rule_number || idx + 1)}
                      </div>
                      <div className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                        {String(rule.explanation || 'No explanation provided')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Passed Rules */}
            <details className="cursor-pointer">
              <summary className="font-medium text-neutral-700 dark:text-neutral-300">
                Passed Rules ({passedRules.length})
              </summary>
              <div className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
                Rules: {passedRules.map(r => r.rule_number).join(', ')}
              </div>
            </details>
          </div>

          {/* Comparison View */}
          <div className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                Comparison View
              </h3>

              <div className="flex gap-2">
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className="flex items-center gap-2 px-4 py-2 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                >
                  <PencilSquareIcon className="w-5 h-5" />
                  {isEditing ? 'View' : 'Edit'}
                </button>

                <button
                  onClick={handleReAnalyze}
                  disabled={isReAnalyzing}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-neutral-400 text-white rounded-lg transition-colors disabled:cursor-not-allowed"
                >
                  <ArrowPathIcon className={`w-5 h-5 ${isReAnalyzing ? 'animate-spin' : ''}`} />
                  {isReAnalyzing ? 'Re-analyzing...' : 'Re-analyze'}
                </button>

                <button
                  onClick={handleDownloadJSON}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                >
                  <ArrowDownTrayIcon className="w-5 h-5" />
                  Download JSON
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Original */}
              <div>
                <h4 className="font-medium text-neutral-700 dark:text-neutral-300 mb-2 flex items-center gap-2">
                  <DocumentTextIcon className="w-5 h-5" />
                  Original Requirement
                </h4>
                <div
                  className="p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 min-h-32 text-neutral-900 dark:text-neutral-100"
                  style={{ whiteSpace: 'pre-wrap' }}
                  dangerouslySetInnerHTML={{ __html: diff.originalHighlighted }}
                />
              </div>

              {/* Improved */}
              <div>
                <h4 className="font-medium text-neutral-700 dark:text-neutral-300 mb-2 flex items-center gap-2">
                  <SparklesIcon className="w-5 h-5" />
                  Improved Requirement
                </h4>
                {isEditing ? (
                  <textarea
                    value={editedImprovement}
                    onChange={(e) => setEditedImprovement(e.target.value)}
                    className="w-full min-h-96 px-4 py-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 focus:ring-2 focus:ring-primary-500 resize-y text-neutral-900 dark:text-neutral-100"
                    style={{ whiteSpace: 'pre-wrap' }}
                  />
                ) : (
                  <div
                    className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 min-h-32 text-neutral-900 dark:text-neutral-100"
                    style={{ whiteSpace: 'pre-wrap' }}
                    dangerouslySetInnerHTML={{ __html: diff.improvedHighlighted }}
                  />
                )}
              </div>
            </div>

            {/* Improvement Rationale */}
            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <h4 className="font-medium text-blue-900 dark:text-blue-300 mb-2">
                Improvement Rationale
              </h4>
              <p className="text-sm text-blue-800 dark:text-blue-400" style={{ whiteSpace: 'pre-wrap' }}>
                {String(currentAnalysis.improvement_rationale || 'No rationale provided')}
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                Note: manual parsing may be needed
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Re-Analysis Results */}
      {reAnalysisResult && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              Verification Results (Improved Requirement Analysis)
            </h3>
          </div>

          <div className="p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg mb-4">
            <div className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Analyzed Text:
            </div>
            <div className="text-sm text-neutral-900 dark:text-neutral-100" style={{ whiteSpace: 'pre-wrap' }}>
              {reAnalysisResult.original_requirement}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                <CheckCircleIcon className="w-6 h-6" />
                <div>
                  <div className="text-2xl font-bold">{reAnalysisResult.passed_rules_count}</div>
                  <div className="text-sm">Passed</div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                <ExclamationTriangleIcon className="w-6 h-6" />
                <div>
                  <div className="text-2xl font-bold">{reAnalysisResult.rule_checks?.filter(r => !r.passed && r.critical !== false).length || 0}</div>
                  <div className="text-sm">Failures</div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                <InformationCircleIcon className="w-6 h-6" />
                <div>
                  <div className="text-2xl font-bold">{reAnalysisResult.rule_checks?.filter(r => !r.passed && r.critical === false).length || 0}</div>
                  <div className="text-sm">Warnings</div>
                </div>
              </div>
            </div>
          </div>

          {/* Comparison with Original Analysis */}
          {currentAnalysis && (
            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <h4 className="font-medium text-blue-900 dark:text-blue-300 mb-2">
                Improvement Progress
              </h4>
              <div className="text-sm text-blue-800 dark:text-blue-400">
                {reAnalysisResult.failed_rules_count < currentAnalysis.failed_rules_count ? (
                  <span className="flex items-center gap-2">
                    <CheckCircleIcon className="w-5 h-5 text-green-600" />
                    Improved! Resolved {currentAnalysis.failed_rules_count - reAnalysisResult.failed_rules_count} issue
                    {currentAnalysis.failed_rules_count - reAnalysisResult.failed_rules_count > 1 ? 's' : ''}
                  </span>
                ) : reAnalysisResult.failed_rules_count === currentAnalysis.failed_rules_count ? (
                  <span className="flex items-center gap-2">
                    Same number of issues - may need more refinement
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <ExclamationTriangleIcon className="w-5 h-5 text-amber-600" />
                    More rule issues than original - review changes
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Failed Rules (Critical) in Re-Analysis */}
          {reAnalysisResult.rule_checks.filter(r => !r.passed && r.critical !== false).length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium text-red-700 dark:text-red-400 mb-2">Remaining Failures (Critical):</h4>
              <div className="space-y-2">
                {reAnalysisResult.rule_checks.filter(r => !r.passed && r.critical !== false).map((rule, idx) => (
                  <div key={idx} className="p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="font-medium text-sm text-red-900 dark:text-red-300">
                      Rule {String(rule.rule_number || idx + 1)}
                    </div>
                    <div className="text-sm text-red-700 dark:text-red-400 mt-1">
                      {String(rule.explanation || 'No explanation provided')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Warning Rules (Non-Critical) in Re-Analysis */}
          {reAnalysisResult.rule_checks.filter(r => !r.passed && r.critical === false).length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium text-amber-700 dark:text-amber-400 mb-2">Remaining Warnings (Non-Critical):</h4>
              <div className="space-y-2">
                {reAnalysisResult.rule_checks.filter(r => !r.passed && r.critical === false).map((rule, idx) => (
                  <div key={idx} className="p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <div className="font-medium text-sm text-amber-900 dark:text-amber-300">
                      Rule {String(rule.rule_number || idx + 1)}
                    </div>
                    <div className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                      {String(rule.explanation || 'No explanation provided')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* All Rules Passed! */}
          {reAnalysisResult.failed_rules_count === 0 && (
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                <CheckCircleIcon className="w-6 h-6" />
                <div className="font-medium">
                  🎉 Success! All rules passed. The requirement is ready to use.
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* History Sidebar */}
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ opacity: 0, x: 300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 300 }}
            className="fixed right-0 top-16 h-[calc(100vh-4rem)] w-96 bg-white dark:bg-neutral-900 border-l border-neutral-200 dark:border-neutral-800 shadow-2xl overflow-y-auto p-6 z-50"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                Analysis History
              </h3>
              <button
                onClick={() => setShowHistory(false)}
                className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              {analysisHistory.map((item, idx) => (
                <div
                  key={item.id}
                  onClick={() => loadHistoryItem(item)}
                  className="p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 cursor-pointer hover:border-primary-500 transition-colors"
                >
                  <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">
                    {new Date(item.timestamp).toLocaleString()}
                  </div>
                  <div className="text-sm text-neutral-900 dark:text-neutral-100 line-clamp-2">
                    {item.original_requirement.slice(0, 100)}...
                  </div>
                  <div className="flex gap-2 mt-2">
                    <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
                      {item.passed_rules_count} passed
                    </span>
                    <span className="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded">
                      {item.rule_checks?.filter(r => !r.passed && r.critical !== false).length || 0} failures
                    </span>
                    <span className="text-xs px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded">
                      {item.rule_checks?.filter(r => !r.passed && r.critical === false).length || 0} warnings
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SingleRequirementAnalysis;
